import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Copy, CheckCircle2, ArrowLeft, Sparkles, Facebook, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/SEO';
import { FENCE_TYPES } from '../mock/data';
import { CITIES } from '../data/locations';

const HASHTAGS = [
  '#Shreveport', '#ShreveportLA', '#BossierCity', '#ArkLaTex', '#LouisianaHomes',
  '#FenceContractor', '#FenceCompany', '#DeckBuilder', '#CustomFence',
  '#OutdoorLiving', '#BackyardGoals', '#CurbAppeal', '#SupportLocal',
];

function pickHashtags(service) {
  const base = ['#BetterFenceAndDeck', '#Shreveport', '#ArkLaTex'];
  const svc = (service || '').toLowerCase();
  if (svc.includes('deck')) return [...base, '#DeckBuilder', '#OutdoorLiving', '#BackyardGoals', '#CustomDeck'];
  if (svc.includes('vinyl')) return [...base, '#VinylFence', '#LowMaintenance', '#CurbAppeal'];
  if (svc.includes('iron')) return [...base, '#OrnamentalIron', '#IronFence', '#CustomFence'];
  if (svc.includes('chain')) return [...base, '#ChainLinkFence', '#FenceCompany'];
  if (svc.includes('wood')) return [...base, '#WoodFence', '#PrivacyFence', '#CedarFence', '#FenceCompany'];
  if (svc.includes('ranch') || svc.includes('farm')) return [...base, '#RanchFence', '#FarmFence'];
  return [...base, '#FenceContractor', '#FenceCompany'];
}

function opener(service, city) {
  const svc = service || 'fence';
  const c = city || 'the ArkLaTex';
  const options = [
    `Another one wrapped up in ${c}. 🔨`,
    `Fresh ${svc.toLowerCase()} install we finished this week in ${c}.`,
    `When the homeowner in ${c} said "make it better than the neighbor's" — challenge accepted.`,
    `${c} homeowners: this is what a properly built ${svc.toLowerCase()} looks like.`,
    `Just handed off the keys on this ${svc.toLowerCase()} project in ${c}.`,
  ];
  return options[Math.floor(Math.random() * options.length)];
}

function buildGbpPost({ service, city, description, cta }) {
  const lines = [];
  lines.push(opener(service, city));
  lines.push('');
  if (description) {
    lines.push(description.trim());
    lines.push('');
  }
  const bullets = [];
  if (service) bullets.push(`✔ ${service}`);
  if (city) bullets.push(`✔ Installed in ${city}`);
  bullets.push('✔ Free on-site estimates');
  bullets.push('✔ Fully insured, locally owned');
  lines.push(bullets.join('\n'));
  lines.push('');
  lines.push(cta || '📞 Call or text 318-344-7190 for a free quote.');
  lines.push('🌐 betterfenceanddeck.org');
  return lines.join('\n');
}

function buildFacebookPost({ service, city, description, cta }) {
  const svc = service || 'fence';
  const tags = pickHashtags(service).join(' ');
  const lines = [];
  lines.push(opener(service, city));
  lines.push('');
  if (description) {
    lines.push(description.trim());
    lines.push('');
  }
  lines.push(`If you're in ${city || 'the Shreveport / Bossier area'} and thinking about a new ${svc.toLowerCase()} — this is what we do, every day. Free estimates, real timelines, no runaround.`);
  lines.push('');
  lines.push(cta || '📞 318-344-7190 to get on the schedule.');
  lines.push('');
  lines.push(tags);
  return lines.join('\n');
}

function buildInstagramPost({ service, city, description }) {
  const tags = pickHashtags(service).concat(HASHTAGS).slice(0, 15);
  const uniqueTags = Array.from(new Set(tags));
  const lines = [];
  lines.push(opener(service, city));
  if (description) {
    lines.push('');
    lines.push(description.trim());
  }
  lines.push('');
  lines.push('📞 318-344-7190');
  lines.push('.');
  lines.push('.');
  lines.push('.');
  lines.push(uniqueTags.join(' '));
  return lines.join('\n');
}

function CopyBlock({ label, icon: Icon, text, testId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — please select and copy manually.');
    }
  };
  return (
    <div className="rounded-md border border-white/10 bg-ink-panel overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-ink-soft">
        <div className="flex items-center gap-2 text-cream">
          <Icon className="w-4 h-4 text-gold" />
          <span className="font-display uppercase tracking-wider text-sm">{label}</span>
        </div>
        <button
          onClick={doCopy}
          data-testid={testId}
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors"
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 text-cream/85 text-sm whitespace-pre-wrap font-mono leading-relaxed">
        {text}
      </pre>
    </div>
  );
}

export default function PostGenerator() {
  const [service, setService] = useState('Wood');
  const [city, setCity] = useState('Shreveport, LA');
  const [description, setDescription] = useState('');
  const [cta, setCta] = useState('');
  const [seed, setSeed] = useState(0); // to reroll openers

  const posts = useMemo(() => {
    // seed used to reroll random openers
    // eslint-disable-next-line no-unused-vars
    const _ = seed;
    return {
      gbp: buildGbpPost({ service, city, description, cta }),
      fb: buildFacebookPost({ service, city, description, cta }),
      ig: buildInstagramPost({ service, city, description }),
    };
  }, [service, city, description, cta, seed]);

  return (
    <div className="bg-ink min-h-screen text-cream">
      <SEO
        title="Post Generator | Better Fence & Deck Admin"
        description="Internal tool for generating Google Business Profile, Facebook, and Instagram posts."
        noindex
      />
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-cream/60 hover:text-gold text-sm uppercase tracking-widest mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="eyebrow">Admin &middot; Post Generator</div>
        <h1 className="section-title">Weekly project posts.</h1>
        <p className="section-lede">
          Fill this out once per job. Get ready-to-paste posts for Google Business Profile, Facebook,
          and Instagram &mdash; each written with the right local keywords so Google keeps rewarding you.
        </p>

        <div className="mt-10 grid lg:grid-cols-2 gap-10">
          {/* Inputs */}
          <div className="space-y-5 rounded-md bg-ink-panel border border-white/10 p-6">
            <div>
              <Label className="text-cream/80 text-xs uppercase tracking-widest">Service performed</Label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="post-service-select" className="mt-1.5 bg-ink border-white/15 text-cream">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {FENCE_TYPES.filter((x) => x !== 'Not Sure Yet').map((x) => (
                    <SelectItem key={x} value={x}>
                      {x}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-cream/80 text-xs uppercase tracking-widest">City / Location</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="post-city-select" className="mt-1.5 bg-ink border-white/15 text-cream">
                  <SelectValue placeholder="Choose a city" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => (
                    <SelectItem key={c.slug} value={`${c.name}, ${c.state}`}>
                      {c.name}, {c.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-cream/80 text-xs uppercase tracking-widest">
                Job details (materials, length, style)
              </Label>
              <Textarea
                data-testid="post-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="180 ft of 6-ft cedar privacy fence with two custom double gates. Homeowner picked western red cedar with steel posts."
                className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
              />
            </div>

            <div>
              <Label className="text-cream/80 text-xs uppercase tracking-widest">Call-to-action (optional)</Label>
              <Input
                data-testid="post-cta-input"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder="📞 Call 318-344-7190 for a free quote"
                className="mt-1.5 bg-ink border-white/15 text-cream placeholder:text-cream/30"
              />
            </div>

            <button
              onClick={() => setSeed((s) => s + 1)}
              data-testid="reroll-opener-btn"
              className="btn-outline w-full justify-center"
            >
              <Sparkles className="w-4 h-4" /> Reroll opening line
            </button>

            <div className="text-xs text-cream/50 border-t border-white/10 pt-4">
              <strong className="text-gold">Pro tip:</strong> Upload 3-5 project photos to each post.
              Google Business Profile posts with photos get 2-3x more engagement and boost your local
              ranking.
            </div>
          </div>

          {/* Outputs */}
          <div className="space-y-5">
            <CopyBlock
              label="Google Business Profile"
              icon={MapPin}
              text={posts.gbp}
              testId="copy-gbp-btn"
            />
            <CopyBlock label="Facebook" icon={Facebook} text={posts.fb} testId="copy-fb-btn" />
            <CopyBlock label="Instagram" icon={Sparkles} text={posts.ig} testId="copy-ig-btn" />
          </div>
        </div>
      </div>
    </div>
  );
}
